import { useRef } from "react";
import { Link } from "react-router-dom";
import { themeOf } from "@/pages/designPreview/previewTheme";
import { CardPattern, Icon } from "@/pages/designPreview/PreviewVisuals";

export function PreviewCta({ href = "/#contact", onClick, children = "프로젝트 상담" }) {
  return (
    <a className="dp-cta" href={href} onClick={onClick}>
      {children}
    </a>
  );
}

// Mouse-tracked tilt: cheap perspective transform driven by pointer position
// within the card. rAF-throttled, skipped for touch/reduced-motion.
function useTilt() {
  const ref = useRef(null);
  const raf = useRef(null);
  const reduceRef = useRef(false);

  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el || reduceRef.current) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      el.style.setProperty("--dp-tilt-x", `${(-py * 6).toFixed(2)}deg`);
      el.style.setProperty("--dp-tilt-y", `${(px * 8).toFixed(2)}deg`);
      el.style.setProperty("--dp-glow-mx", `${((px + 0.5) * 100).toFixed(1)}%`);
      el.style.setProperty("--dp-glow-my", `${((py + 0.5) * 100).toFixed(1)}%`);
      raf.current = null;
    });
  };

  const onMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--dp-tilt-x", "0deg");
    el.style.setProperty("--dp-tilt-y", "0deg");
  };

  const bind = () => {
    if (typeof window !== "undefined") {
      reduceRef.current =
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        window.matchMedia("(pointer: coarse)").matches;
    }
    return { ref, onMouseMove, onMouseLeave };
  };

  return bind();
}

export function CapabilityBlock({ row, index, capstone = false }) {
  const theme = themeOf(row.id);
  const tilt = useTilt();
  return (
    <article
      className={`dp-block${capstone ? " dp-block--capstone" : ""}`}
      id={`layer-${row.id}`}
      ref={tilt.ref}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
      style={{ "--dp-node-hue": theme.hue }}
    >
      <CardPattern kind={row.id} accent={theme.hue} />
      <span className="dp-block__edge" />
      <div className="dp-block__icon">
        <Icon kind={row.id} accent={theme.hue} />
      </div>
      <div className="dp-block__body">
        <p className="dp-kicker">0{index + 1}</p>
        <h3>{row.label}</h3>
        <p>{row.summary}</p>
        <div className="dp-block__links">
          {row.href ? <Link to={row.href}>Service</Link> : null}
          {(row.guide_hrefs || []).slice(0, 1).map((href) => (
            <Link key={href} to={href}>Guide</Link>
          ))}
        </div>
      </div>
    </article>
  );
}
